/* ============================================================
   LUXURY THEME — interactive layer
   ----------------------------------------------------------------
   - Tracks the cursor inside each .card-wrapper to drive a soft
     "spotlight" via CSS variables (--lx-mx, --lx-my) so the navy
     glow follows the mouse, like the focused tile in image 1.
   - Stagger-indexes cards (sets --lx-i) for nicer cascade reveals.
   - Scroll-reveal helper for any element with [data-lx-reveal].
   - Adds a ".lx-scrolled" class to <html> after the user scrolls
     so the header can darken further.
   - Smooth-scrolls in-page anchor links.
   ============================================================ */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  const supportsMatch = 'matches' in Element.prototype;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- 1. Cursor-following spotlight on cards ----------
  function bindSpotlight(card) {
    if (card.__lxBound) return;
    card.__lxBound = true;

    let raf = 0;
    let lastX = 50;
    let lastY = 0;

    function onMove(e) {
      const rect = card.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      lastX = x; lastY = y;
      if (!raf) {
        raf = requestAnimationFrame(() => {
          card.style.setProperty('--lx-mx', lastX + '%');
          card.style.setProperty('--lx-my', lastY + '%');
          raf = 0;
        });
      }
    }

    function onLeave() {
      card.style.setProperty('--lx-mx', '50%');
      card.style.setProperty('--lx-my', '0%');
    }

    card.addEventListener('pointermove', onMove);
    card.addEventListener('pointerleave', onLeave);
  }

  // ---------- 2. Stagger index for cascade fade-up ----------
  function indexCards(scope) {
    const groups = (scope || document).querySelectorAll(
      '.product-grid, .grid--2-col-tablet-down, .grid--3-col, .grid--4-col, ' +
      '.collection-list .grid, .featured-collection .grid, .multicolumn .grid'
    );
    groups.forEach(grp => {
      const items = grp.querySelectorAll(':scope > li, :scope > .grid__item');
      items.forEach((it, i) => it.style.setProperty('--lx-i', i));
    });
  }

  // ---------- 3. Scroll reveal ----------
  let io = null;
  function setupReveal() {
    if (reducedMotion || !('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-lx-reveal]').forEach(el => el.classList.add('is-visible'));
      return;
    }
    io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    document.querySelectorAll('[data-lx-reveal]').forEach(el => io.observe(el));
  }

  // Auto-tag elements that benefit from a reveal animation
  function autoMarkReveals() {
    const sel = [
      '.banner__content',
      '.rich-text__wrapper',
      '.title-wrapper-with-link',
      '.collection__title-wrapper',
      '.multicolumn-card',
      '.contact__fields',
      '.product__title',
      '.product__media-wrapper'
    ].join(',');
    document.querySelectorAll(sel).forEach(el => {
      if (!el.hasAttribute('data-lx-reveal')) {
        el.setAttribute('data-lx-reveal', '');
      }
    });
  }

  // ---------- 4. Scrolled header class ----------
  function bindScroll() {
    let pending = false;
    function update() {
      const y = window.scrollY || window.pageYOffset;
      document.documentElement.classList.toggle('lx-scrolled', y > 8);
      pending = false;
    }
    window.addEventListener('scroll', () => {
      if (!pending) {
        pending = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });
    update();
  }

  // ---------- 5. Smooth in-page anchors ----------
  function bindAnchors() {
    document.addEventListener('click', e => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href');
      if (id.length < 2) return;
      const target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
  }

  // ---------- 6. Tilt on luxury hover (subtle) ----------
  function bindTilt(card) {
    if (card.__lxTilt) return;
    card.__lxTilt = true;
    let raf = 0;
    let tx = 0, ty = 0;

    function onMove(e) {
      const rect = card.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      tx = py * -3;   // rotateX
      ty = px * 4;    // rotateY
      if (!raf) {
        raf = requestAnimationFrame(() => {
          card.style.transform =
            `translateY(-10px) scale(1.015) perspective(900px) rotateX(${tx}deg) rotateY(${ty}deg)`;
          raf = 0;
        });
      }
    }

    function onLeave() {
      card.style.transform = '';
    }

    card.addEventListener('pointermove', onMove);
    card.addEventListener('pointerleave', onLeave);
  }

  // ---------- 7. Featured products → horizontal marquee ----------
  function setupFeaturedMarquee(scope) {
    const root = scope || document;
    const sections = root.querySelectorAll('.featured-collection, .shopify-section[id*="featured-collection"], .shopify-section[id*="featured_collection"]');
    sections.forEach(section => {
      // Skip if already processed
      if (section.dataset.lxMarquee === '1') return;

      // Don't override Shopify's native slider component — only target plain grids
      const grid = section.querySelector('ul.product-grid, ul.grid.product-grid, .product-grid');
      if (!grid) return;
      if (grid.closest('slider-component')) return;
      if (section.querySelector('slider-component')) return;

      const items = Array.from(grid.children).filter(el =>
        el.tagName === 'LI' || el.classList.contains('grid__item'));
      if (items.length < 2) return;

      section.dataset.lxMarquee = '1';
      section.classList.add('lx-marquee-section');

      // Build the track
      const track = document.createElement('div');
      track.className = 'lx-marquee-track';
      items.forEach(item => track.appendChild(item));

      // Clone the whole set once so translateX(-50%) yields a seamless loop
      items.forEach(item => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.classList.add('lx-marquee-clone');
        track.appendChild(clone);
      });

      // Tune speed by item count: ~6s per visible item baseline
      const duration = Math.max(30, Math.min(120, items.length * 8));
      track.style.setProperty('--lx-marquee-duration', duration + 's');

      grid.appendChild(track);

      // Re-bind hover effects on new clones
      track.querySelectorAll('.card-wrapper').forEach(card => {
        bindSpotlight(card);
        if (!reducedMotion) bindTilt(card);
      });
    });
  }

  // ---------- 8. Stat cards detection ----------
  // Only headings that look like short numeric stats ("10+", "1,000+", "10K+")
  // get the big gold gradient treatment.
  function tagStatCards(scope) {
    const root = scope || document;
    const NUMERIC = /^\s*[\d.,]+\s*[KMB+%]*\s*$/i;
    root.querySelectorAll('.multicolumn-card__info').forEach(info => {
      const h = info.querySelector('h3');
      if (!h) return;
      const txt = (h.textContent || '').trim();
      if (txt.length <= 8 && NUMERIC.test(txt)) {
        info.classList.add('lx-stat');
        const card = info.closest('.multicolumn-card');
        if (card) card.classList.add('lx-stat-card');
      }
    });
  }

  // ---------- bootstrap ----------
  function init(scope) {
    const root = scope || document;
    const cards = root.querySelectorAll('.card-wrapper, .multicolumn-card');
    cards.forEach(card => {
      bindSpotlight(card);
      if (!reducedMotion) bindTilt(card);
    });
    indexCards(root);
    tagStatCards(root);
    setupFeaturedMarquee(root);
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(() => {
    autoMarkReveals();
    setupReveal();
    init(document);
    bindScroll();
    bindAnchors();

    // Re-bind on Shopify section reload (theme editor)
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', e => {
        autoMarkReveals();
        if (io) {
          e.target.querySelectorAll('[data-lx-reveal]').forEach(el => io.observe(el));
        }
        init(e.target);
      });
    }

    // MutationObserver for dynamically inserted cards
    if ('MutationObserver' in window) {
      const mo = new MutationObserver(muts => {
        muts.forEach(m => {
          m.addedNodes.forEach(n => {
            if (!(n instanceof HTMLElement)) return;
            if (supportsMatch && n.matches && n.matches('.card-wrapper, .multicolumn-card')) {
              bindSpotlight(n);
              if (!reducedMotion) bindTilt(n);
            }
            init(n);
          });
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  });
})();
